import { Link, useNavigate } from 'react-router-dom';
import { fmtMoney } from '../../shared/format';
import { Badge, Card, Empty, ErrorBox, Loading, Stat, useAsync } from '../../shared/ui';
import { api } from '../api';
import { CompanyCifs } from '../components/CompanyCifs';
import { Icon } from '../components/Icons';
import { PageHead } from '../components/PageHead';
import { SubscriptionBadge } from '../components/SubscriptionBadge';
import type { AdminTenant, Summary } from '../types';

export function Overview(): JSX.Element {
  const navigate = useNavigate();
  const summary = useAsync(() => api.get<Summary>('/admin/summary'), []);
  const tenants = useAsync(() => api.get<{ tenants: AdminTenant[] }>('/admin/tenants').then((r) => r.tenants), []);

  return (
    <div className="stack lg">
      <PageHead
        title="Resumen"
        description="Los clientes de Provalia y su estado comercial de un vistazo."
        actions={
          <Link className="btn primary" to="/clientes?nuevo=1">
            Nuevo cliente
          </Link>
        }
      />

      {summary.error ? (
        <ErrorBox error={summary.error} onRetry={() => void summary.reload()} />
      ) : (
        <div className="stat-grid">
          <Stat label="Clientes" value={summary.data?.tenants ?? '…'} />
          <Stat label="En prueba" value={summary.data?.byStatus.prueba ?? '…'} />
          <Stat label="Activos" value={summary.data?.byStatus.activo ?? '…'} tone={summary.data?.byStatus.activo ? 'ok' : undefined} />
          <Stat
            label="Suspendidos"
            value={summary.data?.byStatus.suspendido ?? '…'}
            tone={summary.data?.byStatus.suspendido ? 'bad' : undefined}
          />
          <Stat
            label="Ingreso mensual recurrente"
            value={summary.data ? fmtMoney(summary.data.monthlyRecurring) : '…'}
            foot="Clientes activos, sin impuestos"
          />
        </div>
      )}

      <Card title="Clientes" actions={<Link to="/clientes">Ver la tabla completa</Link>}>
        {tenants.loading && !tenants.data ? (
          <Loading />
        ) : tenants.error ? (
          <ErrorBox error={tenants.error} onRetry={() => void tenants.reload()} />
        ) : !tenants.data?.length ? (
          <Empty icon="＋">
            <p>Aún no hay ningún cliente.</p>
            <p className="subtle">Da de alta el primero con «Nuevo cliente»: identificador, túnel del hub y Service Layer.</p>
          </Empty>
        ) : (
          <div className="tenant-grid">
            {tenants.data.map((t) => (
              <article key={t.id} className="tenant-card" onClick={() => navigate(`/clientes/${encodeURIComponent(t.slug)}`)}>
                <div className="row between tenant-card-head">
                  <div className="grow">
                    <h3>
                      <Link to={`/clientes/${encodeURIComponent(t.slug)}`} className="strong-link" onClick={(e) => e.stopPropagation()}>
                        {t.name}
                      </Link>
                    </h3>
                    <span className="mono subtle">{t.slug}</span>
                  </div>
                  <div className="row">
                    <SubscriptionBadge status={t.subscription.status} />
                    {!t.active ? <Badge>Inactivo</Badge> : null}
                  </div>
                </div>
                <a className="tenant-link" href={t.portalUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                  {t.portalUrl.replace(/^https?:\/\//, '')} <Icon name="external" size={13} />
                </a>
                <CompanyCifs companies={t.companies} />
              </article>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
